import { parseCsvText, normalizeCsvHeader } from "./csv.js";
import { EXPENSE_TYPE_IRREGULAR, EXPENSE_TYPE_ONGOING } from "../expenseType.js";
import { inferDateFormat } from "../dateFormatInference.js";

const REQUIRED_EXPENSE_COLUMNS = [
  "amount",
  "date",
  "method",
  "type",
  "supplier",
  "category"
];

function mapHeaders(headerRow) {
  const headerMap = new Map();

  headerRow.forEach((header, index) => {
    const normalized = normalizeCsvHeader(header);
    if (!normalized) {
      return;
    }

    if (headerMap.has(normalized)) {
      throw new Error(`Duplicate CSV header: "${header}"`);
    }

    headerMap.set(normalized, index);
  });

  return headerMap;
}

function requiredColumnIndexes(headerMap) {
  const missingColumns = REQUIRED_EXPENSE_COLUMNS.filter((column) => !headerMap.has(column));
  if (missingColumns.length > 0) {
    throw new Error(`Missing required expense columns: ${missingColumns.join(", ")}`);
  }

  return {
    amount: headerMap.get("amount"),
    date: headerMap.get("date"),
    method: headerMap.get("method"),
    type: headerMap.get("type"),
    supplier: headerMap.get("supplier"),
    category: headerMap.get("category"),
    service: headerMap.get("service")
  };
}

function parseAmount(value) {
  const cleaned = String(value ?? "")
    .replace(/\u20AA/g, "")
    .replace(/ILS/gi, "")
    .replace(/[,\s]/g, "")
    .trim();

  if (!cleaned) {
    throw new Error("Expense amount is required.");
  }

  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Invalid expense amount: "${value}"`);
  }

  return parsed;
}

function toIsoDate(year, month, day, originalValue) {
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new Error(`Invalid expense date: "${originalValue}"`);
  }

  return `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

function parseDateWithFormat(value, inferredFormat) {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    throw new Error("Expense date is required.");
  }

  if (inferredFormat === "isoYmd") {
    const isoMatch = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (!isoMatch) {
      throw new Error(`Unsupported expense date format: "${value}"`);
    }

    return toIsoDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]), value);
  }

  if (inferredFormat === "slashDmy" || inferredFormat === "slashMdy") {
    const slashMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (!slashMatch) {
      throw new Error(`Unsupported expense date format: "${value}"`);
    }

    const first = Number(slashMatch[1]);
    const second = Number(slashMatch[2]);
    const year = Number(slashMatch[3]) >= 100 ? Number(slashMatch[3]) : 2000 + Number(slashMatch[3]);

    return inferredFormat === "slashDmy"
      ? toIsoDate(year, second, first, value)
      : toIsoDate(year, first, second, value);
  }

  if (inferredFormat === "dashDmy" || inferredFormat === "dashMdy") {
    const dashMatch = normalized.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
    if (!dashMatch) {
      throw new Error(`Unsupported expense date format: "${value}"`);
    }

    const first = Number(dashMatch[1]);
    const second = Number(dashMatch[2]);
    const year = Number(dashMatch[3]) >= 100 ? Number(dashMatch[3]) : 2000 + Number(dashMatch[3]);

    return inferredFormat === "dashDmy"
      ? toIsoDate(year, second, first, value)
      : toIsoDate(year, first, second, value);
  }

  throw new Error(`Unsupported expense date format: "${value}"`);
}

function normalizeType(value) {
  const normalized = String(value ?? "").trim().toLocaleLowerCase();

  if (normalized === EXPENSE_TYPE_ONGOING) {
    return EXPENSE_TYPE_ONGOING;
  }

  if (normalized === EXPENSE_TYPE_IRREGULAR) {
    return EXPENSE_TYPE_IRREGULAR;
  }

  throw new Error(`Invalid expense type: "${value}"`);
}

export function parseExpenseImportCsv(text, categoryOptions = []) {
  const rows = parseCsvText(text);
  if (rows.length === 0) {
    throw new Error("The selected CSV is empty.");
  }

  const headerMap = mapHeaders(rows[0]);
  const indexes = requiredColumnIndexes(headerMap);
  const categoryIdsByName = new Map(categoryOptions.map((option) => [option.label, option.value]));
  const inferredDateFormat = inferDateFormat(
    rows.slice(1).map((cells) => cells[indexes.date])
  );

  return rows
    .slice(1)
    .map((cells, rowIndex) => {
      const category = String(cells[indexes.category] ?? "").trim();
      if (!category) {
        return null;
      }

      const categoryId = categoryIdsByName.get(category) ?? "";
      if (categoryOptions.length > 0 && !categoryId) {
        throw new Error(`Unknown expense category on row ${rowIndex + 2}: "${category}"`);
      }

      return {
        date: parseDateWithFormat(cells[indexes.date], inferredDateFormat),
        amount: parseAmount(cells[indexes.amount]),
        method: String(cells[indexes.method] ?? "").trim(),
        type: normalizeType(cells[indexes.type]),
        supplier: String(cells[indexes.supplier] ?? "").trim(),
        categoryId,
        category,
        service: String(cells[indexes.service] ?? "").trim()
      };
    })
    .filter(Boolean);
}
